import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

type MultiSelectionStoreType = {
  selectedIds: string[];
  /**
   * Id of the entity currently opened in the drawer (single selection). Used as
   * an anchor so entering multi-selection with Shift keeps the previously
   * opened entity. React Flow mutates its own `selected` state synchronously on
   * click (before our handlers run), so `getActiveNode()` can no longer recover
   * it at that point.
   */
  anchorId: string | null;
  setAnchor: (id: string | null) => void;
  setSelection: (ids: string[]) => void;
  add: (id: string) => void;
  remove: (id: string) => void;
  removeMany: (ids: string[]) => void;
  toggle: (id: string) => void;
  clear: () => void;
  isSelected: (id: string) => boolean;
};

const useMultiSelection = create<MultiSelectionStoreType>((set, get) => ({
  selectedIds: [],
  anchorId: null,

  setAnchor: (id: string | null) => {
    set({ anchorId: id });
  },

  setSelection: (ids: string[]) => {
    set({ selectedIds: Array.from(new Set(ids)) });
  },

  add: (id: string) => {
    set(({ selectedIds }) =>
      selectedIds.includes(id)
        ? { selectedIds }
        : { selectedIds: [...selectedIds, id] },
    );
  },

  remove: (id: string) => {
    set(({ selectedIds }) => ({
      selectedIds: selectedIds.filter((selectedId) => selectedId !== id),
    }));
  },

  removeMany: (ids: string[]) => {
    if (!ids.length) return;
    const drop = new Set(ids);
    set(({ selectedIds }) => ({
      selectedIds: selectedIds.filter((selectedId) => !drop.has(selectedId)),
    }));
  },

  toggle: (id: string) => {
    set(({ selectedIds }) => ({
      selectedIds: selectedIds.includes(id)
        ? selectedIds.filter((selectedId) => selectedId !== id)
        : [...selectedIds, id],
    }));
  },

  clear: () => {
    if (get().selectedIds.length === 0 && get().anchorId === null) return;
    set({ selectedIds: [], anchorId: null });
  },

  isSelected: (id: string) => get().selectedIds.includes(id),
}));

export const useIsNodeMultiSelected = (id: string) =>
  useMultiSelection((state) => state.selectedIds.includes(id));

export const useMultiSelectionCount = () =>
  useMultiSelection((state) => state.selectedIds.length);

export const useMultiSelectionActions = () =>
  useMultiSelection(
    useShallow((state) => ({
      setAnchor: state.setAnchor,
      setSelection: state.setSelection,
      add: state.add,
      remove: state.remove,
      removeMany: state.removeMany,
      toggle: state.toggle,
      clear: state.clear,
      isSelected: state.isSelected,
    })),
  );

export const {
  getState: getMultiSelectionState,
  setState: setMultiSelectionState,
} = useMultiSelection;

export default useMultiSelection;
